import { useState } from "react";
import ChevronDown from "../../svg/chevron-down";

interface ConfigCardAccordionContentProps {
  title: string;
  description: string;
  width?: string;
  children?: React.ReactNode;
  defaultOpen?: boolean;
}

const ConfigCardAccordionContent = ({
  title,
  description,
  width,
  children,
  defaultOpen = false,
}: ConfigCardAccordionContentProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const hasChildren = Boolean(children);

  const handleToggle = () => {
    if (!hasChildren) return;
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      className={`overflow-hidden rounded-md border border-primaryGray-14 ${width ?? "w-full"}`}
    >
      <div
        role={hasChildren ? "button" : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        className={`flex flex-col gap-1 p-3 ${hasChildren ? "cursor-pointer" : ""}`}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (!hasChildren) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div
              className="text-xs font-normal text-primaryGray-4"
              title={title}
            >
              {title}
            </div>
            <div className="truncate text-sm font-semibold" title={description}>
              {description}
            </div>
          </div>
          {hasChildren && (
            <ChevronDown
              stroke="#0F0F0F"
              height={14}
              width={14}
              rotate={isOpen ? 180 : 0}
            />
          )}
        </div>
      </div>

      {hasChildren && (
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <div
              aria-hidden={!isOpen}
              className={`border-t border-primaryGray-14 bg-primaryGray-16 transition-opacity duration-300 ease-in-out ${
                isOpen ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="ml-4 p-3">{children}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigCardAccordionContent;
